<script lang="ts">
	import { SvelteDate } from "svelte/reactivity";
	import { slide } from "svelte/transition";
	import { placeSuggestionService } from "../../core/services/PlaceSuggestService";
	import type { PlaceSuggestion } from "../../core/models/places.types";

	let startDate = new SvelteDate();
	let endDate = new SvelteDate( startDate.getTime() + 24 * 60 * 60 * 1000); // default to next day

	let minimumStartDateString = new Date().toISOString().split("T")[0]; // fixed, never changes
	let minimumEndDateString = $derived(startDate.toISOString().split("T")[0]); // always >= startDate
	let startDateString = $derived(startDate.toISOString().split("T")[0]);
	let endDateString = $derived(endDate.toISOString().split("T")[0]);

	let destination: string = $state("");
	let destinationId: string = $state("");
	let adultCount = $state(1);
	let childCount = $state(0);
	let petCount = $state(0);
	let guestDetailsVisible: boolean = $state(false);
	let guestSummary = $derived(
		`${adultCount} adult${adultCount !== 1 ? "s" : ""}, ${childCount} child${childCount !== 1 ? "ren" : ""}` +
			(petCount > 0 ? `, ${petCount} pet${petCount !== 1 ? "s" : ""}` : ""),
	);

	let suggestions: PlaceSuggestion[] = $state([]);
	let loading: boolean = $state(false);
	let showSuggestions: boolean = $state(false);
	let debounceTimer: ReturnType<typeof setTimeout> | undefined;
	let componentRoot: HTMLElement;

	$effect(() => {
		const query = destination;

		// Clear if user selected a suggestion (destinationId is set) or too short
		if (query.length < 3) {
			suggestions = [];
			showSuggestions = false;
			loading = false;
			return;
		}

		// Don't search again if user just picked a suggestion
		if (destinationId) return;

		loading = true;
		showSuggestions = true;

		clearTimeout(debounceTimer);
		debounceTimer = setTimeout(async () => {
			try {
				suggestions = await placeSuggestionService.getPlaceSuggestions({
					query,
					limit: 5,
				});
			} catch {
				suggestions = [];
			} finally {
				loading = false;
			}
		}, 300);

		return () => {
			clearTimeout(debounceTimer);
			placeSuggestionService.cancelPendingRequest();
		};
	});

	function handlClickOutsideComponent(event: MouseEvent) {
		const path = event.composedPath();
		if (!path.includes(componentRoot)) {
			showSuggestions = false;
			guestDetailsVisible = false;
		}
	}

	function selectSuggestion(suggestion: PlaceSuggestion) {
		destination = suggestion.name;
		destinationId = suggestion.identifier;
		suggestions = [];
		showSuggestions = false;
	}

	function onStartDateChange(event: Event) {
		const val = (event.target as HTMLInputElement).value;
		if (val) {
			const [y, m, d] = val.split("-").map(Number);
			startDate.setFullYear(y, m - 1, d);
			if (startDate > endDate) {
				endDate.setDate(startDate.getDate() + 1);
				endDateString = endDate.toISOString().split("T")[0];
				// if start date changes to after end date, move end date to match start date and update minimum end date
				minimumEndDateString = endDate.toISOString().split("T")[0];
			}else {
				// just update minimum end date
				minimumEndDateString = startDate.toISOString().split("T")[0];
				
			}
		}
	}

	function onEndDateChange(event: Event) {
		const val = (event.target as HTMLInputElement).value;
		if (val) {
			const [y, m, d] = val.split("-").map(Number);
			endDate.setFullYear(y, m - 1, d);
		}
	}

	function onDestinationInput() {
		// Reset selection when user types again
		destinationId = "";
	}
</script>

<!-- Method 1: Direct binding -->
<svelte:window on:click={handlClickOutsideComponent} />

<div id="search" class="w-full" bind:this={componentRoot}>
	<div class="bg-white shadow-lg rounded-xl p-2 border border-gray-300">
		<form method="GET" action="/search">
			<div class="flex flex-col md:flex-row md:items-stretch gap-1">
				<!-- Destination -->
				<div
					class="flex-1 px-3 py-1.5 md:border-r border-gray-200 relative"
				>
					<label
						class="block text-[10px] font-semibold uppercase tracking-wider text-gray-500"
						>Where</label
					>
					<div class="relative">
						<input
							name="destination"
							type="text"
							bind:value={destination}
							oninput={onDestinationInput}
							onfocus={() => {
								if (suggestions.length > 0)
									showSuggestions = true;
							}}
							placeholder="Destination"
							class="w-full text-sm bg-transparent focus:outline-none placeholder-gray-400 pr-6"
							autocomplete="off"
						/>
						{#if loading}
							<div
								class="absolute right-0 top-1/2 -translate-y-1/2"
							>
								<svg
									class="animate-spin h-8 w-8 text-primary"
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
								>
									<circle
										class="opacity-25"
										cx="12"
										cy="12"
										r="10"
										stroke="currentColor"
										stroke-width="4"
									></circle>
									<path
										class="opacity-75"
										fill="currentColor"
										d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
									></path>
								</svg>
							</div>
						{/if}
					</div>
					<input
						name="destinationId"
						type="hidden"
						value={destinationId}
					/>

					{#if showSuggestions && suggestions.length > 0}
						<ul
							class="absolute z-20 left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto"
						>
							{#each suggestions as suggestion (suggestion.identifier)}
								<li>
									<button
										type="button"
										class="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors"
										onclick={() =>
											selectSuggestion(suggestion)}
									>
										<span class="font-medium"
											>{suggestion.name}</span
										>
										{#if suggestion.description}
											<span class="text-gray-400 ml-1"
												>{suggestion.description}</span
											>
										{/if}
									</button>
								</li>
							{/each}
						</ul>
					{/if}
				</div>

				<!-- Check-in -->
				<div class="flex-1 px-3 py-1.5 md:border-r border-gray-200">
					<label
						class="block text-[10px] font-semibold uppercase tracking-wider text-gray-500"
						>Check-in</label
					>
					<input
						name="ds"
						type="date"
						min={minimumStartDateString}
						value={startDateString}
						onchange={onStartDateChange}
						class="w-full text-sm bg-transparent focus:outline-none text-gray-700"
					/>
				</div>

				<!-- Check-out -->
				<div class="flex-1 px-3 py-1.5 md:border-r border-gray-200">
					<label
						class="block text-[10px] font-semibold uppercase tracking-wider text-gray-500"
						>Check-out</label
					>
					<input
						name="de"
						type="date"
						value={endDateString}
						onchange={onEndDateChange}
						min={minimumEndDateString}
						class="w-full text-sm bg-transparent focus:outline-none text-gray-700"
					/>
				</div>

				<!-- Guests -->
				<div class="flex-1 px-3 py-1.5 relative">
					<label
						class="block text-[10px] font-semibold uppercase tracking-wider text-gray-500"
						>Guests</label
					>
					<button
						type="button"
						onclick={() =>
							(guestDetailsVisible = !guestDetailsVisible)}
						class="w-full text-sm text-left text-gray-700 flex justify-between items-center"
					>
						<span>{guestSummary}</span>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width="12"
							height="12"
							fill="currentColor"
							viewBox="0 0 16 16"
						>
							<path
								fill-rule="evenodd"
								d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"
							/>
						</svg>
					</button>

					<input name="ad" type="hidden" value={adultCount} />
					<input name="ch" type="hidden" value={childCount} />
					<input name="pt" type="hidden" value={petCount} />

					{#if guestDetailsVisible}
						<div
							transition:slide|local={{
								duration: 200,
								axis: "y",
							}}
							class="absolute z-10 right-0 top-full mt-2 bg-white shadow-xl rounded-lg p-4 w-full   border border-gray-200"
						>
							<!-- Adults -->
							<div class="flex justify-between items-center mb-3">
								<div>
									<p class="text-sm font-medium">Adults</p>
									<p class="text-xs text-gray-500">
										Ages 13+
									</p>
								</div>
								<div class="flex items-center">
									<button
										type="button"
										class="w-7 h-7 border border-gray-300 rounded-l hover:bg-gray-50 text-sm"
										onclick={() =>
											(adultCount = Math.max(
												1,
												adultCount - 1,
											))}>−</button
									>
									<input
										value={adultCount}
										readonly
										class="w-10 h-7 text-center text-sm border-y border-gray-300"
									/>
									<button
										type="button"
										class="w-7 h-7 border border-gray-300 rounded-r hover:bg-gray-50 text-sm"
										onclick={() =>
											(adultCount = Math.min(
												16,
												adultCount + 1,
											))}>+</button
									>
								</div>
							</div>

							<!-- Children -->
							<div class="flex justify-between items-center">
								<div>
									<p class="text-sm font-medium">Children</p>
									<p class="text-xs text-gray-500">
										Ages 2-12
									</p>
								</div>
								<div class="flex items-center">
									<button
										type="button"
										class="w-7 h-7 border border-gray-300 rounded-l hover:bg-gray-50 text-sm"
										onclick={() =>
											(childCount = Math.max(
												0,
												childCount - 1,
											))}>−</button
									>
									<input
										value={childCount}
										readonly
										class="w-10 h-7 text-center text-sm border-y border-gray-300"
									/>
									<button
										type="button"
										class="w-7 h-7 border border-gray-300 rounded-r hover:bg-gray-50 text-sm"
										onclick={() =>
											(childCount = Math.min(
												5,
												childCount + 1,
											))}>+</button
									>
								</div>
							</div>

							<!-- Pets -->
							<div class="flex justify-between items-center mt-3">
								<div>
									<p class="text-sm font-medium">Pets</p>
									<p class="text-xs text-gray-500">
										Bringing an animal?
									</p>
								</div>
								<div class="flex items-center">
									<button
										type="button"
										class="w-7 h-7 border border-gray-300 rounded-l hover:bg-gray-50 text-sm"
										onclick={() =>
											(petCount = Math.max(
												0,
												petCount - 1,
											))}>−</button
									>
									<input
										value={petCount}
										readonly
										class="w-10 h-7 text-center text-sm border-y border-gray-300"
									/>
									<button
										type="button"
										class="w-7 h-7 border border-gray-300 rounded-r hover:bg-gray-50 text-sm"
										onclick={() =>
											(petCount = Math.min(
												5,
												petCount + 1,
											))}>+</button
									>
								</div>
							</div>

							<div
								class="flex justify-end mt-3 pt-3 border-t border-gray-200"
							>
								<button
									type="button"
									class="text-xs font-medium text-blue-600 hover:text-blue-700"
									onclick={() =>
										(guestDetailsVisible = false)}
									>Done</button
								>
							</div>
						</div>
					{/if}
				</div>

				<!-- Search button -->
				<button
					name="action"
					value="search"
					class="flex items-center justify-center gap-2 px-5 py-2.5 bg-primary text-white rounded-lg hover:accent-primary font-semibold text-sm transition-colors"
					aria-label="Search"
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="16"
						height="16"
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
						/>
					</svg>
					<span class="md:hidden lg:inline">Search</span>
				</button>
			</div>
		</form>
	</div>
</div>
